/**
 * Buchungsanfragen für Fotoboxen.
 *
 * Ablauf (bewusst als Zustandsmaschine, weil an jedem Übergang Mails hängen):
 *
 *   requested   Kunde hat unverbindlich angefragt
 *                 → Vermieter bekommt Mail mit Annehmen/Ablehnen-Link
 *   accepted    Vermieter hat angenommen
 *                 → Kunde bekommt Mail mit Zahlungslink
 *   declined    Vermieter hat abgelehnt
 *                 → Kunde bekommt Absage mit Hinweis auf Alternativen
 *   expired     Vermieter hat nicht reagiert (nach Reminder-Kette)
 *                 → beide bekommen Mail, Kunde sucht neu
 *   paid        Anzahlung ist bei uns eingegangen
 *                 → beide bekommen die Kontaktdaten der Gegenseite
 *   cancelled   nach Zahlung storniert (manuell durch uns)
 *
 * Die Anzahlung entspricht der Vermittlungsprovision. Der Restbetrag läuft
 * direkt zwischen Kunde und Vermieter — wir fassen ihn nie an.
 */

export const BOOKING_STATUS = {
  REQUESTED: "requested",
  ACCEPTED: "accepted",
  DECLINED: "declined",
  EXPIRED: "expired",
  PAID: "paid",
  CANCELLED: "cancelled",
};

export const EVENT_TYPES = [
  "Hochzeit",
  "Geburtstag",
  "Abschluss / Abi",
  "Firmenevent",
  "Weihnachtsfeier",
  "Anderer Anlass",
];

export const DELIVERY_METHODS = {
  POST: "post",
  DELIVERY: "delivery",
  PICKUP: "pickup",
};

export const DELIVERY_LABELS = {
  post: "Versand per Post",
  delivery: "Lieferung durch den Anbieter",
  pickup: "Abholung beim Anbieter",
};

/**
 * Fristen für die Antwort des Vermieters.
 *
 * Nach außen kommunizieren wir 24 Stunden — das hält den Kunden nicht tagelang
 * hin. Wer die Frist verstreichen lässt, bekommt einmalig 12 Stunden Nachfrist
 * (danach ist endgültig Schluss). So ist der Fall nach spätestens anderthalb
 * Tagen entschieden statt nach drei.
 *
 *    12 h → 1. Erinnerung
 *    20 h → 2. Erinnerung
 *    24 h → kommunizierte Frist abgelaufen, Nachfrist von 12 h beginnt
 *    30 h → letzte Erinnerung
 *    36 h → Anfrage verfällt
 */
export const RESPONSE_WINDOW_HOURS = 24;
export const GRACE_WINDOW_HOURS = 12;
export const EXPIRE_AFTER_HOURS =
  RESPONSE_WINDOW_HOURS + GRACE_WINDOW_HOURS; // 36

/** Wann welche Nachricht rausgeht — Stunde und Art. */
export const RESPONSE_STEPS = [
  { hours: 12, kind: "partnerReminder", label: "1. Erinnerung" },
  { hours: 20, kind: "partnerReminder", label: "2. Erinnerung" },
  { hours: 24, kind: "partnerGrace", label: "Nachfrist gewährt" },
  { hours: 30, kind: "partnerLastCall", label: "letzte Erinnerung" },
];

/**
 * Zahlungsfrist des Kunden nach der Zusage.
 *
 * Der Vermieter sagt zu und hält den Termin damit verbindlich frei — deshalb
 * darf die Zusage nicht unbefristet offen bleiben. Läuft die Frist ab, wird
 * die Anfrage geschlossen und der Termin ist für den Vermieter wieder frei.
 */
export const PAYMENT_WINDOW_HOURS = 24;

/** Erinnerung an den Kunden, bevor seine Zahlungsfrist abläuft. */
export const PAYMENT_REMINDER_HOURS = 18;

export function paymentDeadlineFrom(acceptedAtIso) {
  return new Date(
    new Date(acceptedAtIso).getTime() + PAYMENT_WINDOW_HOURS * 36e5,
  ).toISOString();
}

export function formatDateTimeDE(iso) {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
  })} um ${d.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
  })} Uhr`;
}

/** Kurze, gut vorlesbare ID — taucht in Mails und im Admin auf. */
export function generateBookingId() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // ohne I/O/0/1
  let out = "";
  for (let i = 0; i < 6; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return `FB-${out}`;
}

/**
 * Rechnet den Preis einer Anfrage aus.
 *
 * Anfahrtskosten bleiben bewusst außen vor — sie hängen an der echten
 * Entfernung und werden vom Anbieter separat abgerechnet. Genau so steht es
 * auch im bisherigen Checkout.
 */
export function calculateTotals({
  boothPrice = 0,
  packagePrice = 0,
  extras = [],
  deliveryCost = 0,
  commissionPercent = 0,
}) {
  const extrasTotal = extras.reduce(
    (sum, e) => sum + (e.price || 0) * (e.quantity || 0),
    0,
  );
  // Lieferkosten zählen mit — egal ob Versand per Post oder Anfahrt des
  // Anbieters. Der Kunde soll einen Endpreis sehen, keine Überraschung nach
  // der Buchung. Entsprechend fällt auch die Provision darauf an.
  const total =
    (boothPrice || 0) + (packagePrice || 0) + extrasTotal + (deliveryCost || 0);

  // Anzahlung = Vermittlungsprovision, kaufmännisch auf Cent gerundet.
  const depositRaw = (total * (commissionPercent || 0)) / 100;
  const deposit = Math.round(depositRaw * 100) / 100;

  return {
    boothPrice: boothPrice || 0,
    packagePrice: packagePrice || 0,
    extrasTotal,
    deliveryCost: deliveryCost || 0,
    total,
    deposit,
    remainder: Math.round((total - deposit) * 100) / 100,
    commissionPercent: commissionPercent || 0,
  };
}

export function formatEUR(value) {
  return `${Number(value || 0).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

export function formatDateDE(iso) {
  if (!iso) return "–";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Offene Anfragen, bei denen der Vermieter noch reagieren muss. */
export function isAwaitingPartner(booking) {
  return booking.status === BOOKING_STATUS.REQUESTED;
}

export function hoursSince(iso) {
  if (!iso) return 0;
  return (Date.now() - new Date(iso).getTime()) / 36e5;
}
